import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold, Italic, Underline, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Link as LinkIcon, Unlink, Minus, Undo, Redo, RemoveFormatting,
} from 'lucide-react';

const ToolbarButton = ({ onClick, active, disabled, title, children, testId }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testId}
    className={`p-2 rounded transition-colors disabled:opacity-30 ${
      active ? 'bg-primary text-white' : 'hover:bg-slate-200 text-slate-700'
    }`}
  >
    {children}
  </button>
);

const Divider = () => <span className="w-px h-6 bg-slate-300 mx-1" />;

const RichTextEditor = ({ value, onChange, placeholder = 'Start writing your article...' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } },
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'prose prose-slate max-w-none min-h-[320px] p-4 outline-none',
        'data-testid': 'blog-editor-content',
        'data-placeholder': placeholder,
      },
    },
    onUpdate: ({ editor: ed }) => onChange(ed.isEmpty ? '' : ed.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML() && !(value === '' && editor.isEmpty)) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Enter URL', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
  };

  const c = editor.chain().focus();

  return (
    <div className="border border-slate-300 rounded-md overflow-hidden bg-white" data-testid="blog-rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 bg-slate-50 border-b border-slate-300 px-2 py-1.5">
        <ToolbarButton testId="editor-undo" title="Undo" onClick={() => c.undo().run()} disabled={!editor.can().undo()}><Undo size={16} /></ToolbarButton>
        <ToolbarButton testId="editor-redo" title="Redo" onClick={() => c.redo().run()} disabled={!editor.can().redo()}><Redo size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton testId="editor-h1" title="Heading 1" active={editor.isActive('heading', { level: 1 })} onClick={() => c.toggleHeading({ level: 1 }).run()}><Heading1 size={16} /></ToolbarButton>
        <ToolbarButton testId="editor-h2" title="Heading 2" active={editor.isActive('heading', { level: 2 })} onClick={() => c.toggleHeading({ level: 2 }).run()}><Heading2 size={16} /></ToolbarButton>
        <ToolbarButton testId="editor-h3" title="Heading 3" active={editor.isActive('heading', { level: 3 })} onClick={() => c.toggleHeading({ level: 3 }).run()}><Heading3 size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton testId="editor-bold" title="Bold (Ctrl+B)" active={editor.isActive('bold')} onClick={() => c.toggleBold().run()}><Bold size={16} /></ToolbarButton>
        <ToolbarButton testId="editor-italic" title="Italic (Ctrl+I)" active={editor.isActive('italic')} onClick={() => c.toggleItalic().run()}><Italic size={16} /></ToolbarButton>
        <ToolbarButton testId="editor-underline" title="Underline (Ctrl+U)" active={editor.isActive('underline')} onClick={() => c.toggleUnderline().run()}><Underline size={16} /></ToolbarButton>
        <ToolbarButton testId="editor-strike" title="Strikethrough" active={editor.isActive('strike')} onClick={() => c.toggleStrike().run()}><Strikethrough size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton testId="editor-bullet-list" title="Bullet list" active={editor.isActive('bulletList')} onClick={() => c.toggleBulletList().run()}><List size={16} /></ToolbarButton>
        <ToolbarButton testId="editor-ordered-list" title="Numbered list" active={editor.isActive('orderedList')} onClick={() => c.toggleOrderedList().run()}><ListOrdered size={16} /></ToolbarButton>
        <ToolbarButton testId="editor-blockquote" title="Quote" active={editor.isActive('blockquote')} onClick={() => c.toggleBlockquote().run()}><Quote size={16} /></ToolbarButton>
        <ToolbarButton testId="editor-hr" title="Horizontal rule" onClick={() => c.setHorizontalRule().run()}><Minus size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton testId="editor-link" title="Insert link" active={editor.isActive('link')} onClick={setLink}><LinkIcon size={16} /></ToolbarButton>
        <ToolbarButton testId="editor-unlink" title="Remove link" disabled={!editor.isActive('link')} onClick={() => c.unsetLink().run()}><Unlink size={16} /></ToolbarButton>
        <ToolbarButton testId="editor-clear" title="Clear formatting" onClick={() => c.clearNodes().unsetAllMarks().run()}><RemoveFormatting size={16} /></ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
